import { onlyDigits } from "@/lib/strings";
import { clientGet } from "@/lib/http/client-http";

export interface CepAddress {
  street: string;
  neighborhood: string;
  city: string;
  state: string;
}

/** Distinguishes "not found" from "service failure" for contextual UI feedback. */
export type CepLookupResult =
  | { status: "found"; address: CepAddress }
  | { status: "not_found" }
  | { status: "unavailable" };

type CepLookupResponse =
  | { ok: true; address: CepAddress }
  | { ok: false; code: string };

/**
 * Looks up a CEP via the BFF route. Returns a discriminated result so the
 * form can show contextual messages (CEP not found vs service unavailable).
 *
 * Never throws — errors are captured and returned as `unavailable`.
 * Accepts an optional `AbortSignal` so callers can cancel stale lookups.
 */
export async function lookupCep(rawCep: string, signal?: AbortSignal): Promise<CepLookupResult> {
  const cep = onlyDigits(rawCep);
  try {
    const data = await clientGet<CepLookupResponse>(`/api/cep/${cep}`, { signal });
    if (data.ok) return { status: "found", address: data.address };
    if (data.code === "NOT_FOUND") return { status: "not_found" };
    // SERVICE_UNAVAILABLE or any other error code from the BFF.
    return { status: "unavailable" };
  } catch {
    // Network failure, timeout, or abort — treat as unavailable.
    return { status: "unavailable" };
  }
}
